import { Icon } from '@/components/ui/Icon'
import { Loader } from '@/components/ui/Loader'
import { Map as MapComponent, MapMarker } from '@/components/ui/Map'
import { formatAddress } from '@/lib/address'
import { getDonationSpecification } from '@/lib/donation'
import { StopCard } from '@/pages/Route/StopCard'
import { useRouteStore } from '@/store/route'
import { StandardDonationAasmState } from '@types'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styles from './Route.module.scss'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_API_KEY || ''

export const RoutePage = () => {
  const navigate = useNavigate()
  const { partOfDay } = useParams<{ partOfDay: string }>()
  const [isMapVisible, setIsMapVisible] = useState(true)

  const sortedDonations = useRouteStore((state) => state.sortedDonations)
  const donationsLoading = useRouteStore((state) => state.donationsLoading)
  const fetchDonations = useRouteStore((state) => state.fetchDonations)
  const selectedDate = useRouteStore((state) => state.selectedDate)
  const dispatchAddress = useRouteStore((state) => state.dispatchAddress)
  const returnAddress = useRouteStore((state) => state.returnAddress)

  const isFutureDate = moment(selectedDate).isAfter(moment(), 'day')

  useEffect(() => {
    fetchDonations()
  }, [fetchDonations])
  const isAm = partOfDay?.toLowerCase() === 'am'

  // Filter sorted donations by part of day
  const donationsWithManifestIndex = useMemo(
    () =>
      sortedDonations.filter(
        ({ donation }) => donation.part_of_day === (isAm ? 'am' : 'pm')
      ),
    [sortedDonations, isAm]
  )

  const routeTitle = partOfDay?.toUpperCase() === 'PM' ? 'PM Route' : 'AM Route'
  const totalJobs = donationsWithManifestIndex.length

  // Dispatch/return addresses from manifest
  const dispatchAddressStr = formatAddress(dispatchAddress ?? undefined)
  const returnAddressStr = formatAddress(returnAddress ?? undefined)

  // Get the last manifest index for the return location
  const lastManifestIndex =
    donationsWithManifestIndex.length > 0
      ? donationsWithManifestIndex[donationsWithManifestIndex.length - 1]
          .manifestIndex
      : 0

  // Create map markers
  const mapMarkers: MapMarker[] = [
    // Dispatch location
    ...((isAm ||
      sortedDonations.length === donationsWithManifestIndex.length) &&
    dispatchAddress
      ? [
          {
            id: 0,
            stepNumber: 0,
            status: 'dispatch' as const,
            lat: dispatchAddress.lat ?? 0,
            lng: dispatchAddress.lng ?? 0,
          },
        ]
      : []),
    // Donation stops (using manifest index for step numbers)
    ...donationsWithManifestIndex.map(({ donation, manifestIndex }) => ({
      id: donation.id,
      stepNumber: manifestIndex,
      status: donation.aasm_state ?? StandardDonationAasmState.ENQUEUED,
      lat: donation.address?.lat ?? 0,
      lng: donation.address?.lng ?? 0,
    })),
    // Return location
    ...((!isAm ||
      sortedDonations.length === donationsWithManifestIndex.length) &&
    returnAddress
      ? [
          {
            id: -1,
            stepNumber: lastManifestIndex + 1,
            status: 'return' as const,
            lat: returnAddress.lat ?? 0,
            lng: returnAddress.lng ?? 0,
          },
        ]
      : []),
  ]

  const handleToggleMap = () => {
    setIsMapVisible(!isMapVisible)
  }

  const handleBack = () => {
    navigate('/')
  }

  const mapSectionClasses = [
    styles.mapSection,
    isMapVisible ? styles.mapExpanded : styles.mapCollapsed,
  ]
    .filter(Boolean)
    .join(' ')

  const bottomSheetClasses = [
    styles.bottomSheet,
    !isMapVisible && styles.sheetFullHeight,
  ]
    .filter(Boolean)
    .join(' ')

  const toggleIconClasses = [
    styles.toggleIcon,
    !isMapVisible && styles.iconFlipped,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={styles.container} data-testid="route-page">
      {/* Map Section */}
      <div className={mapSectionClasses} data-testid="route-map-section">
        <button
          className={styles.mapBackButton}
          onClick={handleBack}
          data-testid="route-back-button"
        >
          <Icon name="chevron-left" size={20} className={styles.backIcon} />
        </button>
        <MapComponent markers={mapMarkers} accessToken={MAPBOX_TOKEN} />
      </div>

      {/* Bottom Sheet */}
      <div className={bottomSheetClasses} data-testid="route-bottom-sheet">
        {/* Scrollable content */}
        <div className={styles.sheetContent}>
          <div
            className={styles.handle}
            onClick={handleToggleMap}
            data-testid="route-sheet-handle"
          >
            <div className={styles.handleBar} />
          </div>
          {/* Header */}
          <div className={styles.sheetHeader}>
            <div className={styles.headerTop}>
              <h1 className={styles.routeTitle} data-testid="route-title">
                {routeTitle}
              </h1>
              <button
                className={styles.toggleButton}
                onClick={handleToggleMap}
                data-testid="route-toggle-map"
              >
                <Icon
                  name="chevron-down"
                  size={20}
                  className={toggleIconClasses}
                />
              </button>
            </div>

            {/* Stats */}
            <div className={styles.statsRow}>
              <div className={styles.stat}>
                <Icon name="job" size={16} className={styles.statIcon} />
                <span>
                  <span data-testid="route-total-jobs">{totalJobs}</span> jobs
                </span>
              </div>
            </div>
          </div>

          {/* Stops List */}
          {donationsLoading ? (
            <Loader testId="route-loader" />
          ) : (
            <div className={styles.stopsList} data-testid="route-stops-list">
              {/* Dispatch Location */}
              {(isAm ||
                sortedDonations.length === donationsWithManifestIndex.length) &&
                dispatchAddress && (
                  <StopCard
                    stepNumber={0}
                    status="dispatch"
                    address={dispatchAddressStr}
                    isLast={donationsWithManifestIndex.length === 0}
                  />
                )}

              {/* Donation Stops (using manifest index for step numbers) */}
              {donationsWithManifestIndex.map(
                ({ donation, manifestIndex }, index) => (
                  <StopCard
                    key={donation.id}
                    stepNumber={manifestIndex}
                    status={
                      donation.aasm_state ?? StandardDonationAasmState.ENQUEUED
                    }
                    donationId={donation.id}
                    name={donation.donor?.name}
                    address={formatAddress(donation.address)}
                    specification={getDonationSpecification(donation.items)}
                    phone={donation.donor?.phone}
                    isReported={!!donation.report}
                    isFutureDate={isFutureDate}
                    isLast={
                      index === donationsWithManifestIndex.length - 1 &&
                      !(
                        (!isAm ||
                          sortedDonations.length ===
                            donationsWithManifestIndex.length) &&
                        returnAddress
                      )
                    }
                  />
                )
              )}

              {/* Return Location */}
              {(!isAm ||
                sortedDonations.length === donationsWithManifestIndex.length) &&
                returnAddress && (
                  <StopCard
                    stepNumber={lastManifestIndex + 1}
                    status="return"
                    address={returnAddressStr}
                    isLast
                  />
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
